// src/components/faction/FactionCard.jsx
import React, { useState, useEffect, useMemo } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { useNavigate } from 'react-router-dom'
import { db } from '../../firebase'
import WrestlerGrid from '../wrestler/WrestlerGrid'
import SwapResume from './SwapResume'
import CreditIcon from '../CreditIcon'
import AnimatedNumber from '../ui/AnimatedNumber'
import FactionShareButton from './FactionShareButton'
import {
  formatSignedPoints,
  getLastShowBannerMessage,
  getLastShowContributorBonusMeta
} from '../../utils/lastShowSummary'
import {
  calculateFactionTotalScore,
  getFactionMainEventerStatus,
  hasFactionScoringStarted
} from '../../utils/factionScoring'
import {
  buildDismissibleBannerKey,
  hideDismissibleBanner,
  isDismissibleBannerHidden
} from '../../utils/dismissibleBannerPrefs'

const renderLastShowContributorBonus = entry => {
  const bonusMeta = getLastShowContributorBonusMeta(entry?.status)
  if (!bonusMeta) return null

  return (
    <span
      className={`faction-last-show-banner__member-bonus ${bonusMeta.toneClassName}`}
    >
      {bonusMeta.label}
    </span>
  )
}

function FactionCard ({
  faction,
  members = [],
  onEdit,
  isEditing,
  onSave,
  onCancel,
  formData,
  setFormData,
  isFavorite = false,
  userProfile = null,
  lastShowSummary = null
}) {
  const navigate = useNavigate()
  const [timeLeft, setTimeLeft] = useState(null)
  const [showCountdown, setShowCountdown] = useState(false)
  const [showMainEventerAlert, setShowMainEventerAlert] = useState(true)
  const [injuryBannerClosed, setInjuryBannerClosed] = useState(false)
  const [injuryBannerHidden, setInjuryBannerHidden] = useState(false)
  const [ownerProfile, setOwnerProfile] = useState({
    profileImageUrl: null,
    username: ''
  })
  const [rosterData, setRosterData] = useState(null)
  const effectiveFaction = useMemo(
    () => ({
      ...faction,
      dataInizioPunteggio:
        rosterData?.dataInizioPunteggio || faction.dataInizioPunteggio
    }),
    [faction, rosterData?.dataInizioPunteggio]
  )
  const factionRosterName =
    rosterData?.name || faction.rosterName || faction.roster || 'Non assegnato'
  const factionGameType = rosterData?.gameType || faction.gameType || ''

  // Fetch owner profile
  useEffect(() => {
    const fetchOwnerProfile = async () => {
      if (faction.ownerUid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', faction.ownerUid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            setOwnerProfile({
              username: userData.username || 'Ã¢â‚¬â€',
              profileImageUrl: userData.profileImageUrl || null,
              email: userData.email
            })
          }
        } catch (err) {
          console.error('Errore nel caricamento owner:', err)
        }
      }
    }

    if (faction.ownerUid) {
      fetchOwnerProfile()
    }
  }, [faction.ownerUid])

  useEffect(() => {
    const fetchRosterData = async () => {
      if (!faction.rosterId) {
        setRosterData(null)
        return
      }

      try {
        const rosterDoc = await getDoc(doc(db, 'rosters', faction.rosterId))
        if (rosterDoc.exists()) {
          setRosterData({ id: rosterDoc.id, ...rosterDoc.data() })
        } else {
          setRosterData(null)
        }
      } catch (err) {
        console.error('Errore nel caricamento roster:', err)
        setRosterData(null)
      }
    }

    fetchRosterData()
  }, [faction.rosterId])

  // Effetto per calcolare il countdown
  useEffect(() => {
    const calculateTimeLeft = () => {
      let targetDate = null

      // Controlla se c'ÃƒÂ¨ una data di inizio punteggio futura
      if (effectiveFaction.dataInizioPunteggio) {
        let startDate

        // Converti la data in base al formato
        if (typeof effectiveFaction.dataInizioPunteggio.toDate === 'function') {
          startDate = effectiveFaction.dataInizioPunteggio.toDate()
        } else if (effectiveFaction.dataInizioPunteggio instanceof Date) {
          startDate = effectiveFaction.dataInizioPunteggio
        } else if (
          typeof effectiveFaction.dataInizioPunteggio === 'string' ||
          typeof effectiveFaction.dataInizioPunteggio === 'number'
        ) {
          startDate = new Date(effectiveFaction.dataInizioPunteggio)
        }

        if (startDate && startDate > new Date()) {
          targetDate = startDate
        }
      }

      // Se non c'ÃƒÂ¨ data di inizio punteggio, controlla Road To
      if (!targetDate && factionGameType === 'Road To') {
        const roadToDeadline = new Date('2026-02-01T00:00:00')
        if (new Date() < roadToDeadline) {
          targetDate = roadToDeadline
        }
      }

      if (targetDate) {
        const now = new Date()
        const difference = targetDate.getTime() - now.getTime()

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24))
          const hours = Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          )
          const minutes = Math.floor(
            (difference % (1000 * 60 * 60)) / (1000 * 60)
          )
          const seconds = Math.floor((difference % (1000 * 60)) / 1000)

          setTimeLeft({ days, hours, minutes, seconds })
          setShowCountdown(true)
        } else {
          setShowCountdown(false)
        }
      } else {
        setShowCountdown(false)
      }
    }

    calculateTimeLeft()

    // Aggiorna il countdown ogni secondo
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [effectiveFaction.dataInizioPunteggio, factionGameType])

  // Se in modalitÃƒÂ  editing, usa il componente EditFactionForm
  const isRoadTo = factionGameType === 'Road To'
  const scoringStarted = hasFactionScoringStarted(effectiveFaction)
  const mainEventerStatus = useMemo(
    () =>
      getFactionMainEventerStatus({
        faction: effectiveFaction,
        roster: rosterData
      }),
    [effectiveFaction, rosterData]
  )
  const hasBackstageSlots =
    Boolean(rosterData?.hasBackstage) ||
    Number(rosterData?.backstageCount || 0) > 0
  const injuryBannerKey = useMemo(
    () => buildDismissibleBannerKey('faction-injuries', faction?.id || 'global'),
    [faction?.id]
  )
  const injuredMembers = useMemo(
    () => members.filter(member => member?.isInjured),
    [members]
  )
  const injuredActiveMembers = useMemo(
    () => injuredMembers.filter(member => member?.status !== 'Backstage'),
    [injuredMembers]
  )
  const injuredMembersLabel = useMemo(() => {
    const source =
      injuredActiveMembers.length > 0 ? injuredActiveMembers : injuredMembers
    const names = source
      .map(member => member?.name)
      .filter(Boolean)
      .slice(0, 3)

    if (source.length > 3) {
      names.push(`+${source.length - 3}`)
    }

    return names.join(', ')
  }, [injuredActiveMembers, injuredMembers])
  const showInjuryBanner =
    injuredMembers.length > 0 && !injuryBannerClosed && !injuryBannerHidden

  useEffect(() => {
    setInjuryBannerClosed(false)
    setInjuryBannerHidden(isDismissibleBannerHidden(injuryBannerKey))
  }, [injuryBannerKey])

  const handleHideInjuryBannerPermanently = () => {
    hideDismissibleBanner(injuryBannerKey)
    setInjuryBannerHidden(true)
    setInjuryBannerClosed(true)
  }
  const lastShowContributorsLabel = useMemo(() => {
    if (!Array.isArray(lastShowSummary?.contributors)) return []
    return lastShowSummary.contributors
  }, [lastShowSummary])
  const lastShowGroups = useMemo(() => {
    if (!Array.isArray(lastShowSummary?.showSummaries)) return []
    return lastShowSummary.showSummaries
  }, [lastShowSummary])
  const hasMultipleLastShows = lastShowGroups.length > 1
  const lastShowBannerMessage = useMemo(() => {
    if (!lastShowContributorsLabel.length) {
      return 'Stavolta nessuno della tua fazione ha dato a punti.'
    }

    return getLastShowBannerMessage({
      totalPoints: lastShowSummary?.totalPoints || 0,
      seed: `${faction?.id || ''}:${lastShowSummary?.showId || ''}:profile`
    })
  }, [faction?.id, lastShowContributorsLabel.length, lastShowSummary])

  useEffect(() => {
    setShowMainEventerAlert(true)
  }, [faction.id, mainEventerStatus?.kind])

  if (isEditing) {
    const EditFactionForm = require('./EditFactionForm').default
    return (
      <div>
        <EditFactionForm
          faction={faction}
          members={members}
          formData={formData}
          setFormData={setFormData}
          userProfile={userProfile}
          onCancel={() => {
            console.log('Chiamato onCancel da EditFactionForm')
            if (onCancel) onCancel()
          }}
          onSave={updatedFaction => {
            console.log('Chiamato onSave da EditFactionForm')
            if (onSave) onSave(updatedFaction)
            if (onCancel) onCancel()
          }}
        />
      </div>
    )
  }

  // Calcola punti totali dai membri
  const totalScore = effectiveFaction.isGM
    ? effectiveFaction.totalScore || 0
    : calculateFactionTotalScore({
        faction: effectiveFaction,
        roster: rosterData,
        members
      })

  return (
    <div className='faction-details-card'>
      {/* Header Hero - Versione compatta per card */}
      <div
        className='faction-hero position-relative overflow-hidden py-4 px-3 px-md-4'
        style={{
          borderBottom: '3px solid var(--brand-secondary)',
          borderRadius: 'var(--border-radius) var(--border-radius) 0 0'
        }}
      >
        <div className='position-absolute top-0 start-0 w-100 h-100' />
        {faction.imageUrl && (
          <>
            <div
              className='position-absolute top-0 start-0 w-100 h-100'
              style={{
                background:
                  'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.22) 0%, transparent 34%)',
                zIndex: 0
              }}
            />
            <img
              src={faction.imageUrl}
              alt=''
              aria-hidden='true'
              className='position-absolute top-0 start-0 w-100 h-100'
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'blur(4px) saturate(1.08) brightness(0.8)',
                opacity: 0.4,
                transform: 'scale(1.22)',
                zIndex: 0
              }}
              onError={e => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div
              className='position-absolute top-0 start-0 w-100 h-100'
              style={{
                background: 'linear-gradient(45deg, rgba(38, 56, 72, 0) 100%)',
                zIndex: 1
              }}
            />
          </>
        )}
        <div
          className='container position-relative h-100 faction-card__hero-shell'
          style={{ zIndex: 2 }}
        >
          <div className='row align-items-center h-100'>
            {/* Logo Fazione */}
            <div className='col-12 col-md-3 col-lg-2 mb-3 mb-md-0'>
              <div className='faction-logo-container d-flex align-items-center justify-content-center'>
                {faction.imageUrl ? (
                  <div className='faction-logo-wrapper rounded-3 '>
                    <img
                      src={faction.imageUrl}
                      alt={`Logo ${faction.name}`}
                      className='img-fluid rounded-3 shadow'
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '120px',
                        border: '2px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className='faction-logo-wrapper rounded-3 d-flex align-items-center justify-content-center'
                    style={{
                      width: '120px',
                      height: '120px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '2px dashed rgba(255,255,255,0.3)'
                    }}
                  >
                    <i
                      className='bi bi-people-fill text-white opacity-75'
                      style={{ fontSize: '2.5rem' }}
                    ></i>
                  </div>
                )}
              </div>
            </div>

            {/* Info Centro */}
            <div className='col-12 col-md-6 col-lg-7 mb-3 mb-md-0 ps-lg-4'>
              <div className='position-relative mb-3'>
                {/* Contenitore centrato per il nome */}
                <div className='d-flex justify-content-center'>
                  <h3
                    className='faction-name fw-bold mb-2'
                    style={{ color: 'white', fontSize: '1.8rem' }}
                  >
                    {faction.name}
                  </h3>
                </div>
              </div>
              <div className='faction-info justify-content-center text-center text-md-start'>
                {/* Badges */}
                <div className='faction-badges d-flex flex-wrap gap-2 mb-3 justify-content-center justify-content-md-start'>
                  {/* Roster Badge - White/Light */}
                  <span
                    className='badge rounded-pill px-3 py-1 d-inline-flex align-items-center cursor-pointer'
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.68)',
                      color: '#111',
                      border: '1px solid var(--brand-primary)',
                      fontSize: '0.85rem',
                      backdropFilter: 'blur(10px)'
                    }}
                    onClick={() => {
                      if (faction.rosterId) {
                        navigate(`/rosters/${faction.rosterId}`)
                      }
                    }}
                  >
                    <i className='bi bi-diagram-2-fill me-1'></i>
                    {factionRosterName}
                  </span>

                  {/* GameType Badge - Rosso se Road To, Primary se Indipendente, Secondary se Competitivo */}
                  <span
                    className='badge rounded-pill px-3 py-1 d-inline-flex align-items-center'
                    style={{
                      backgroundColor:
                        factionGameType === 'Road To'
                          ? 'rgba(220, 53, 69, 0.18)'
                          : factionGameType === 'Indipendente'
                          ? 'rgba(255,255,255,0.14)'
                          : factionGameType === 'Competitivo'
                          ? 'rgba(var(--brand-secondary), 0.16)'
                          : 'rgba(255, 255, 255, 0.14)',
                      color:
                        factionGameType === 'Road To'
                          ? '#dc3545'
                          : factionGameType === 'Indipendente'
                          ? '#fff'
                          : factionGameType === 'Competitivo'
                          ? 'var(--brand-secondary)'
                          : '#fff',
                      border:
                        factionGameType === 'Road To'
                          ? '1px solid #dc3545'
                          : factionGameType === 'Indipendente'
                          ? '1px solid rgba(255,255,255,0.24)'
                          : factionGameType === 'Competitivo'
                          ? '1px solid var(--brand-secondary)'
                          : '1px solid rgba(255,255,255,0.24)',
                      fontSize: '0.85rem',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <i className={`bi bi-joystick me-1`}></i>
                    {factionGameType || 'N/A'}
                  </span>

                  {/* Credits Badge - Unchanged */}
                  <span
                    className='badge rounded-pill px-3 py-1 d-inline-flex align-items-center'
                    style={{
                      backgroundColor: 'rgba(0, 128, 17, 0.25)',
                      color: 'var(--money)',
                      border: '1px solid var(--money)',
                      fontSize: '0.85rem',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <CreditIcon className='me-1' size={16} />
                    <AnimatedNumber value={faction.creditsLeft || 0} />
                  </span>

                  {/* Mostra la data di inizio punteggio se esiste */}
                  {effectiveFaction.dataInizioPunteggio && (
                    <span
                      className='badge rounded-pill px-3 py-1 d-inline-flex align-items-center'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.24)',
                        fontSize: '0.85rem',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <i className='bi bi-calendar-event me-1'></i>
                      Inizio:{' '}
                      {(() => {
                        let dateObj
                        if (
                          typeof effectiveFaction.dataInizioPunteggio
                            ?.toDate === 'function'
                        ) {
                          dateObj =
                            effectiveFaction.dataInizioPunteggio.toDate()
                        } else if (
                          typeof effectiveFaction.dataInizioPunteggio ===
                            'string' ||
                          typeof effectiveFaction.dataInizioPunteggio ===
                            'number'
                        ) {
                          dateObj = new Date(
                            effectiveFaction.dataInizioPunteggio
                          )
                        } else if (
                          effectiveFaction.dataInizioPunteggio instanceof Date
                        ) {
                          dateObj = effectiveFaction.dataInizioPunteggio
                        }

                        if (dateObj instanceof Date && !isNaN(dateObj)) {
                          return dateObj.toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short'
                          })
                        }
                        return 'Data non valida'
                      })()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Punteggio */}
            <div className='col-12 col-md-3 col-lg-3'>
              <div
                className='total-score-card text-center p-3 rounded-4 shadow'
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '3px solid white',
                  color: 'var(--light)',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '140px',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 16px 36px rgba(0,0,0,0.22)'
                }}
              >
                <div className='position-relative'>
                  {showCountdown ? (
                    <div className='countdown-container position-relative p-3 rounded-4'>
                      {/* Etichetta */}
                      <div
                        className='countdown-label text-center mb-3'
                        style={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '0.9rem',
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          fontWeight: 500
                        }}
                      >
                        Il punteggio partirà tra
                      </div>

                      {/* Countdown */}
                      <div className='countdown-display d-flex justify-content-center align-items-center gap-2 gap-md-3'>
                        {timeLeft ? (
                          <>
                            {/* Giorni (se presenti) */}
                            {timeLeft.days > 0 && (
                              <div className='time-unit text-center'>
                                <div
                                  className='time-value d-flex align-items-center justify-content-center'
                                  style={{
                                    fontSize: '2rem',
                                    fontWeight: 800,
                                    borderRadius: '12px',
                                    padding: '0.5rem',
                                    minWidth: '1rem',
                                    textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                    boxShadow:
                                      '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                    fontFamily: 'monospace',
                                    letterSpacing: '1px'
                                  }}
                                >
                                  {timeLeft.days}
                                </div>
                                <div
                                  className='time-label mt-1'
                                  style={{
                                    fontSize: '0.65rem',
                                    color: 'rgba(255,255,255,0.5)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  Giorni
                                </div>
                              </div>
                            )}

                            {/* Ore */}
                            <div className='time-unit text-center'>
                              <div
                                className='time-value d-flex align-items-center justify-content-center'
                                style={{
                                  fontSize: '2rem',
                                  fontWeight: 800,
                                  borderRadius: '12px',
                                  padding: '0.5rem',
                                  minWidth: '1rem',
                                  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                  boxShadow:
                                    '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                  fontFamily: 'monospace',
                                  letterSpacing: '1px'
                                }}
                              >
                                {timeLeft.hours.toString().padStart(2, '0')}
                              </div>
                              <div
                                className='time-label mt-1'
                                style={{
                                  fontSize: '0.65rem',
                                  color: 'rgba(255,255,255,0.5)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                Ore
                              </div>
                            </div>

                            {/* Minuti */}
                            <div className='time-unit text-center'>
                              <div
                                className='time-value d-flex align-items-center justify-content-center'
                                style={{
                                  fontSize: '2rem',
                                  fontWeight: 800,
                                  borderRadius: '12px',
                                  padding: '0.5rem',
                                  minWidth: '1rem',
                                  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                  boxShadow:
                                    '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                  fontFamily: 'monospace',
                                  letterSpacing: '1px'
                                }}
                              >
                                {timeLeft.minutes.toString().padStart(2, '0')}
                              </div>
                              <div
                                className='time-label mt-1'
                                style={{
                                  fontSize: '0.65rem',
                                  color: 'rgba(255,255,255,0.5)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                Minuti
                              </div>
                            </div>

                            {/* Secondi */}
                            <div className='time-unit text-center'>
                              <div
                                className='time-value d-flex align-items-center justify-content-center'
                                style={{
                                  fontSize: '2rem',
                                  fontWeight: 800,
                                  borderRadius: '12px',
                                  padding: '0.5rem',
                                  minWidth: '1rem',
                                  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                  boxShadow:
                                    '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                  fontFamily: 'monospace',
                                  letterSpacing: '1px'
                                }}
                              >
                                {timeLeft.seconds.toString().padStart(2, '0')}
                              </div>
                              <div
                                className='time-label mt-1'
                                style={{
                                  fontSize: '0.65rem',
                                  color: 'rgba(255,255,255,0.5)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                Secondi
                              </div>
                            </div>
                          </>
                        ) : (
                          <div
                            className='text-center py-4'
                            style={{
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                              fontStyle: 'italic'
                            }}
                          >
                            Calcolo in corso...
                          </div>
                        )}
                      </div>

                      {/* Elemento decorativo */}
                      <div
                        className='position-absolute top-0 start-0 w-100 h-100 overflow-hidden rounded-4'
                        style={{
                          pointerEvents: 'none',
                          zIndex: 0
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: '-50%',
                            right: '-30%',
                            width: '200px',
                            height: '200px',
                            background: isRoadTo
                              ? 'radial-gradient(circle, var(--brand-secondary-transparent) 0%, transparent 70%)'
                              : 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                            filter: 'blur(20px)',
                            opacity: 0.5
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className='score-container position-relative p-0'>
                      <div
                        className='score-label text-center mb-2'
                        style={{
                          color: 'var(--brand-secondary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          opacity: 0.8
                        }}
                      >
                        Punteggio
                      </div>

                      <div
                        className='score-value text-center'
                        style={{
                          color: '#FFFFFF',
                          fontSize: 'clamp(3rem, 8vw, 4rem)',
                          fontWeight: 700,
                          textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          position: 'relative'
                        }}
                      >
                        <AnimatedNumber
                          value={totalScore !== undefined ? totalScore : 0}
                        />

                        {/* Linea sottile sotto il numero */}
                        <div
                          className='position-absolute bottom-0 start-50 translate-middle-x'
                          style={{
                            width: '60px',
                            height: '2px',
                            background: 'var(--brand-secondary)',
                            opacity: 0.5
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='faction-card__actions'>
            <FactionShareButton
              faction={effectiveFaction}
              members={members}
              roster={rosterData}
              buttonClassName='btn btn-light btn-sm'
              title='Condividi la tua fazione'
              iconOnly
            />
            {onEdit && (
              <button
                className='btn btn-outline-brand-secondary btn-sm'
                onClick={onEdit}
                title='Modifica fazione'
                type='button'
              >
                <i className='bi bi-pencil-fill'></i>
              </button>
            )}
          </div>
        </div>

        {showInjuryBanner && (
          <div className='container mt-3'>
            <div className='faction-injury-banner m-0' role='alert'>
              <div className='faction-injury-banner__content'>
                <i className='bi bi-bandaid-fill' aria-hidden='true'></i>
                <div>
                  <strong>
                    {hasBackstageSlots && injuredActiveMembers.length > 0
                      ? 'Sposta gli infortunati in backstage'
                      : 'Hai wrestler infortunati in fazione'}
                  </strong>
                  <span>
                    {hasBackstageSlots && injuredActiveMembers.length > 0 ? (
                      <>
                        Hai {injuredActiveMembers.length}{' '}
                        {injuredActiveMembers.length === 1
                          ? 'wrestler infortunato ancora attivo'
                          : 'wrestler infortunati ancora attivi'}
                        {injuredMembersLabel
                          ? `: ${injuredMembersLabel}.`
                          : '.'}{' '}
                        {injuredActiveMembers.length === 1
                          ? 'Spostalo in backstage dalla line-up per non lasciarlo schierato.'
                          : 'Spostali in backstage dalla line-up per non lasciarli schierati.'}
                      </>
                    ) : (
                      <>
                        Hai {injuredMembers.length}{' '}
                        {injuredMembers.length === 1
                          ? 'wrestler infortunato'
                          : 'wrestler infortunati'}
                        {injuredMembersLabel
                          ? `: ${injuredMembersLabel}.`
                          : '.'}
                      </>
                    )}
                  </span>
                </div>
              </div>
              <div className='faction-injury-banner__actions'>
                <button
                  type='button'
                  className='btn btn-sm btn-outline-light'
                  onClick={handleHideInjuryBannerPermanently}
                >
                  Non mostrare piu
                </button>
                <button
                  type='button'
                  className='btn-close btn-close-white'
                  aria-label='Chiudi'
                  onClick={() => setInjuryBannerClosed(true)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Alert Main Eventer scaduto - Richiudibile */}
        {mainEventerStatus?.kind === 'expired' && showMainEventerAlert && (
          <div className='container mt-3'>
            <div
              className='m-0 d-flex align-items-center justify-content-between rounded-3 p-2 text-light'
              role='alert'
              style={{
                background: 'rgba(255, 193, 7, 0.16)',
                border: '1px solid rgba(255, 193, 7, 0.75)',
                fontSize: '0.8rem',
                backdropFilter: 'blur(2px)'
              }}
            >
              <div className='d-flex align-items-center flex-wrap gap-2'>
                <i className='bi bi-exclamation-triangle-fill text-warning me-1'></i>
                <div>
                  Il main eventer della tua fazione è scaduto. Aggiorna la
                  line-up per continuare a ottenere punti.
                </div>
                {faction.id && (
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-warning'
                    onClick={() => navigate(`/factions/${faction.id}/lineup`)}
                  >
                    Vai alla line-up
                  </button>
                )}
              </div>
              <button
                type='button'
                className='btn-close btn-close-white'
                aria-label='Chiudi'
                onClick={() => setShowMainEventerAlert(false)}
                style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Contenuto Principale */}
      <div className='container'>
        <div className='row'>
          {/* Sezione Membri */}
          <div className='col-12 p-0 mt-2'>
            {members?.length > 0 ? (
              <>
                <WrestlerGrid
                  members={members}
                  faction={effectiveFaction}
                  roster={rosterData}
                  factionName={faction.name}
                  scoringStarted={scoringStarted}
                />
                {scoringStarted && (
                  <div className='faction-last-show-banner mt-3'>
                    <div className='faction-last-show-banner__top'>
                      <div className='faction-last-show-banner__copy'>
                        {hasMultipleLastShows ? (
                          <div className='faction-last-show-banner__shows'>
                            {lastShowGroups.map(showGroup => (
                              <div
                                key={`${showGroup.showId || showGroup.showName}-${showGroup.totalPoints}`}
                                className='faction-last-show-banner__show'
                              >
                                <div className='faction-last-show-banner__show-head'>
                                  <span className='faction-last-show-banner__show-name'>
                                    {showGroup.showName || 'Show'}
                                  </span>
                                  <strong
                                    className={`faction-last-show-banner__show-total ${showGroup.totalPoints < 0 ? 'is-negative' : ''}`}
                                  >
                                    {formatSignedPoints(showGroup.totalPoints)}
                                  </strong>
                                </div>
                                <div className='faction-last-show-banner__members'>
                                  {showGroup.contributors.map(entry => (
                                    <div
                                      key={`${showGroup.showId}-${entry.id}-${entry.points}`}
                                      className='faction-last-show-banner__member'
                                    >
                                      <div className='faction-last-show-banner__member-copy'>
                                        <span className='faction-last-show-banner__member-name'>
                                          {entry.name}
                                        </span>
                                      </div>
                                      <strong
                                        className={`faction-last-show-banner__member-points ${entry.points < 0 ? 'is-negative' : ''}`}
                                      >
                                        <span>{formatSignedPoints(entry.points)}</span>
                                        {renderLastShowContributorBonus(entry)}
                                      </strong>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : lastShowContributorsLabel.length > 0 ? (
                          <div className='faction-last-show-banner__members'>
                            {lastShowContributorsLabel.map(entry => (
                              <div
                                key={`${entry.id}-${entry.points}`}
                                className='faction-last-show-banner__member'
                              >
                                <div className='faction-last-show-banner__member-copy'>
                                  <span className='faction-last-show-banner__member-name'>
                                    {entry.name}
                                  </span>
                                </div>
                                <strong
                                  className={`faction-last-show-banner__member-points ${entry.points < 0 ? 'is-negative' : ''}`}
                                >
                                  <span>{formatSignedPoints(entry.points)}</span>
                                  {renderLastShowContributorBonus(entry)}
                                </strong>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className='mb-0 faction-last-show-banner__empty'>
                            Nessun wrestler della tua fazione ha fatto punti nell ultimo show
                          </p>
                        )}
                      </div>
                      <div
                        className={`faction-last-show-banner__score ${lastShowSummary?.totalPoints < 0 ? 'is-negative' : ''}`}
                      >
                        <span className='faction-last-show-banner__score-label'>
                          Totale
                        </span>
                        <strong>
                          {formatSignedPoints(lastShowSummary?.totalPoints || 0)}
                        </strong>
                      </div>
                    </div>
                    <div className='faction-last-show-banner__message'>
                      {lastShowBannerMessage}
                    </div>
                  </div>
                )}
                {faction.createdAt && (
                  <div className='text-center small mt-3'>
                    <small className='text-muted-brand'>
                      <i className='bi bi-calendar me-1'></i>
                      Creata il{' '}
                      {(() => {
                        let dateObj
                        if (typeof faction.createdAt?.toDate === 'function') {
                          dateObj = faction.createdAt.toDate()
                        } else if (
                          typeof faction.createdAt === 'string' ||
                          typeof faction.createdAt === 'number'
                        ) {
                          dateObj = new Date(faction.createdAt)
                        } else if (faction.createdAt instanceof Date) {
                          dateObj = faction.createdAt
                        }
                        if (dateObj instanceof Date && !isNaN(dateObj)) {
                          return dateObj.toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        } else {
                          return 'Data non disponibile'
                        }
                      })()}
                    </small>
                  </div>
                )}
              </>
            ) : (
              <div className='alert alert-warning m-0'>
                <i className='bi bi-exclamation-triangle me-2'></i>
                La tua fazione non ha ancora wrestler.
              </div>
            )}
          </div>

          {/* Storia degli scambi */}
          <div className='col-12 mt-4'>
            <div className='card border-0 shadow-sm'>
              <div className='card-body p-0'>
                <SwapResume factionId={faction.id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FactionCard
